import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/form-controls";
import { toast } from "@/components/ui/toast";
import { CopyableReference, SupportDetails } from "@/components/ui/copyable-reference";
import { usePermission } from "@/hooks/use-permission";
import { usePlainErrorMessage } from "@/components/data-display/api-failure";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import {
  fetchConfigSettings,
  updateConfigSettings,
  fetchConfigBranding,
  updateConfigBranding,
  fetchNotificationPreferences,
  updateNotificationPreferences,
  type ConfigSettingsSnapshot,
  type ConfigBrandingSnapshot,
  type NotificationPreferences,
  type NotificationChannelConfig,
  type ApiErrorInfo,
  type ApiDetailResponse,
} from "@/lib/api";

/* SET-00 / SET-02 — Settings Home / Notification Preferences.
   Canonical source: prototype/pages/admin/settings.html. The prototype's
   Organization tab (org name, legal name, org id, primary domain, timezone,
   country, logo upload) and its 6-item flat Notifications checklist have NO
   backend behind almost any of those exact fields (ALL-4738 G1/G2):
     - GET/PUT /v1/config/settings only allow-lists
       transfer.auto_approve_threshold, transfer.default_currency,
       notifications.email_enabled, session.idle_timeout_minutes
       (TenantConfigSchema, alloy-api-gateway-service).
     - GET/PUT /v1/config/branding only allow-lists logoUrl (an https asset
       URL, not a file upload), primaryColor (one of 4 design tokens) and
       companyName. There is no orgId, legalName, primaryDomain, timezone or
       country concept anywhere on the backend.
     - GET/PUT /v1/notifications/preferences is keyed by backend event type
       (transfer.submitted, approval.required, approval.resolved,
       transfer.failed — JdbcNotificationInboxService.ALLOWED_EVENT_TYPES),
       each with independent email/inApp/webhook booleans — not the
       prototype's 6 named toggles.
   Per this page's "no fabricated UI" rule, the fields with no backend are
   rendered as an explicit "not available yet" notice instead of editable
   inputs, and the tabs below are wired to the real, narrower schema. */

type Tab = "organization" | "notifications";

const TAB_LABELS: Record<Tab, string> = {
  organization: "Organization",
  notifications: "Notifications",
};

const CURRENCY_OPTIONS = ["USDC", "USDT", "USD"];
/* Internal design-token names are the stored values (unchanged, and what the
   PUT sends), but they are never shown to a user: the select displays the
   human label. Copy-only change, no value or save-path change. */
const PRIMARY_COLOR_OPTIONS = [
  { value: "alloy.brand.primary", label: "Brand blue" },
  { value: "alloy.brand.subtle", label: "Brand subtle" },
  { value: "alloy.semantic.info", label: "Info" },
  { value: "alloy.semantic.success", label: "Success" },
];
const NOT_AVAILABLE_FIELDS = [
  { label: "Legal Name" },
  { label: "Organization ID" },
  { label: "Primary Domain" },
  { label: "Timezone" },
  { label: "Country" },
];

const EVENT_TYPE_LABELS: Record<string, { label: string; desc: string }> = {
  "transfer.submitted": { label: "Transfer submitted", desc: "A transfer was drafted or submitted for processing" },
  "approval.required": { label: "Approval required", desc: "A transfer or action entered pending-approval state" },
  "approval.resolved": { label: "Approval resolved", desc: "An approval was approved, rejected, or held" },
  "transfer.failed": { label: "Transfer failed", desc: "A transfer failed or was rejected downstream" },
};

function eventTypeLabel(eventType: string): { label: string; desc: string } {
  return EVENT_TYPE_LABELS[eventType] ?? { label: eventType, desc: "" };
}

/**
 * Reports a transport failure — the request never reached the gateway, so
 * there is no HTTP status, no body and no support reference to quote — in the
 * fetchers' own `{ item, error }` shape.
 *
 * `fetchDetail` in `lib/api.ts` does not catch a rejected `fetch`, so a bare
 * `await` lets the rejection escape the loader: `setLoading(false)` is never
 * reached and the tab sits on "Loading…" forever (with an unhandled
 * rejection), which is the one state the page must never be left in.
 * `NETWORK_ERROR` is the same reason code the other loaders report for it
 * (see `features/valuation/use-valuation-summary.ts`).
 */
function catchTransportFailure<T>(request: Promise<ApiDetailResponse<T>>): Promise<ApiDetailResponse<T>> {
  return request.catch(() => ({ item: null, error: { reasonCode: "NETWORK_ERROR" } }));
}

function isDenied(error: ApiErrorInfo | null | undefined): boolean {
  if (!error) return false;
  return error.reasonCode === "HTTP_401" || error.reasonCode === "HTTP_403"
    || error.reasonCode === "AUTH_INSUFFICIENT_SCOPE" || error.reasonCode === "AUTH_CONTEXT_INVALID";
}

export function Settings({ initialTab }: { initialTab: Tab }) {
  const plainError = usePlainErrorMessage();
  const [tab, setTab] = useState<Tab>(initialTab);
  const canReadSettings = usePermission("settings:read");
  const canEditSettings = usePermission("settings:full");
  const canReadNotifications = usePermission("notifications-prefs:read");
  const canEditNotifications = usePermission("notifications-prefs:full");

  // --- Organization tab: /v1/config/settings + /v1/config/branding ---
  const [settingsSnapshot, setSettingsSnapshot] = useState<ConfigSettingsSnapshot | null>(null);
  const [brandingSnapshot, setBrandingSnapshot] = useState<ConfigBrandingSnapshot | null>(null);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgError, setOrgError] = useState<ApiErrorInfo | null>(null);
  // The denied response itself, so the notice can show its reference and capability.
  const [orgDenied, setOrgDenied] = useState<ApiErrorInfo | null>(null);

  const [autoApproveThreshold, setAutoApproveThreshold] = useState(0);
  const [defaultCurrency, setDefaultCurrency] = useState("USDC");
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [idleTimeout, setIdleTimeout] = useState(30);
  const [companyName, setCompanyName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [primaryColor, setPrimaryColor] = useState(PRIMARY_COLOR_OPTIONS[0].value);
  const [orgSaving, setOrgSaving] = useState(false);
  const [orgStale, setOrgStale] = useState(false);

  const loadOrg = useCallback(async () => {
    setOrgLoading(true);
    setOrgError(null);
    setOrgDenied(null);
    const [settingsResult, brandingResult] = await Promise.all([
      catchTransportFailure(fetchConfigSettings()),
      catchTransportFailure(fetchConfigBranding()),
    ]);
    if (settingsResult.error && isDenied(settingsResult.error)) {
      setOrgDenied(settingsResult.error);
    } else if (settingsResult.error) {
      setOrgError(settingsResult.error);
    } else if (settingsResult.item) {
      setSettingsSnapshot(settingsResult.item);
      const values = settingsResult.item.settings ?? {};
      setAutoApproveThreshold(Number(values["transfer.auto_approve_threshold"] ?? 1000));
      setDefaultCurrency(String(values["transfer.default_currency"] ?? "USDC"));
      setEmailEnabled(Boolean(values["notifications.email_enabled"] ?? true));
      setIdleTimeout(Number(values["session.idle_timeout_minutes"] ?? 30));
    }
    if (!settingsResult.error) {
      if (brandingResult.error && !isDenied(brandingResult.error)) {
        setOrgError(brandingResult.error);
      } else if (brandingResult.item) {
        setBrandingSnapshot(brandingResult.item);
        setCompanyName(brandingResult.item.companyName ?? "");
        setLogoUrl(brandingResult.item.logoUrl ?? "");
        setPrimaryColor(brandingResult.item.primaryColor ?? PRIMARY_COLOR_OPTIONS[0].value);
      }
    }
    setOrgLoading(false);
  }, []);

  // --- Notifications tab: /v1/notifications/preferences ---
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [channels, setChannels] = useState<Record<string, NotificationChannelConfig>>({});
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifError, setNotifError] = useState<ApiErrorInfo | null>(null);
  const [notifDenied, setNotifDenied] = useState<ApiErrorInfo | null>(null);
  const [notifSaving, setNotifSaving] = useState(false);

  const loadNotifications = useCallback(async () => {
    setNotifLoading(true);
    setNotifError(null);
    setNotifDenied(null);
    const result = await catchTransportFailure(fetchNotificationPreferences());
    if (result.error && isDenied(result.error)) {
      setNotifDenied(result.error);
    } else if (result.error) {
      setNotifError(result.error);
    } else if (result.item) {
      setPreferences(result.item);
      setChannels(result.item.channels ?? {});
    }
    setNotifLoading(false);
  }, []);

  useEffect(() => {
    loadOrg();
    loadNotifications();
  }, [loadOrg, loadNotifications]);

  async function saveOrg() {
    if (!settingsSnapshot || !brandingSnapshot) return;
    setOrgSaving(true);
    setOrgStale(false);
    const [settingsResult, brandingResult] = await Promise.all([
      updateConfigSettings(
        {
          "transfer.auto_approve_threshold": autoApproveThreshold,
          "transfer.default_currency": defaultCurrency,
          "notifications.email_enabled": emailEnabled,
          "session.idle_timeout_minutes": idleTimeout,
        },
        settingsSnapshot.version,
      ),
      updateConfigBranding(
        { logoUrl: logoUrl.trim() ? logoUrl.trim() : null, primaryColor, companyName },
        brandingSnapshot.version,
      ),
    ]);
    setOrgSaving(false);
    // TenantConfigService throws AlloyApiException(DUPLICATE_REQUEST, "Configuration
    // version conflict", {reasonCode: "config_version_conflict"}). apiMutate's error
    // string comes from the RFC 7807 envelope's top-level `reason_code`
    // (AlloyProblem.reasonCode(), = the enum name "DUPLICATE_REQUEST"), not the
    // nested `details.reasonCode` business code — `message` is absent (the field
    // is `detail`, not `message`), so "DUPLICATE_REQUEST" is what actually comes
    // back on a real version conflict, not the string "config_version_conflict".
    const conflict = settingsResult.error === "DUPLICATE_REQUEST" || brandingResult.error === "DUPLICATE_REQUEST";
    if (conflict) {
      setOrgStale(true);
      toast({
        variant: "warning",
        title: "Settings changed elsewhere",
        description: "Reload to see the latest values before saving again.",
        supportReference: settingsResult.supportReference ?? brandingResult.supportReference,
      });
      return;
    }
    if (!settingsResult.ok || !brandingResult.ok) {
      const reason = settingsResult.error === "Network error" || brandingResult.error === "Network error"
        ? "The save may or may not have gone through — reload to check before retrying."
        : (settingsResult.error || brandingResult.error || "Save failed");
      // The reference of the request that failed; settings is checked first.
      const failed = !settingsResult.ok ? settingsResult : brandingResult;
      toast({
        variant: "error",
        title: "Failed to save settings",
        description: reason,
        supportReference: failed.supportReference,
        capability: failed.capability,
      });
      return;
    }
    // Two PUTs make one save; the settings PUT's reference identifies it.
    toast({ variant: "success", title: "Settings saved", supportReference: settingsResult.supportReference ?? brandingResult.supportReference });
    await loadOrg();
  }

  async function saveNotifications() {
    setNotifSaving(true);
    const result = await updateNotificationPreferences(channels);
    setNotifSaving(false);
    if (!result.ok) {
      const reason = result.error === "Network error"
        ? "The save may or may not have gone through — reload to check before retrying."
        : (result.error || "Save failed");
      toast({
        variant: "error",
        title: "Failed to save notification preferences",
        description: reason,
        supportReference: result.supportReference,
        capability: result.capability,
      });
      return;
    }
    toast({ variant: "success", title: "Notification preferences saved", supportReference: result.supportReference });
    await loadNotifications();
  }

  const EMPTY_CHANNEL: NotificationChannelConfig = { email: false, inApp: false, webhook: false };

  function setChannel(eventType: string, field: keyof NotificationChannelConfig, value: boolean) {
    setChannels((prev) => {
      const current = prev[eventType] ?? EMPTY_CHANNEL;
      return { ...prev, [eventType]: { ...current, [field]: value } };
    });
  }

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-extrabold text-ink">Settings</h1>
      </div>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-[200px_1fr]">
        <nav className="flex gap-1.5 overflow-x-auto md:flex-col md:overflow-visible">
          {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`shrink-0 rounded-md px-3 py-2 text-left text-sm font-medium md:w-full ${
                tab === t ? "bg-primary-soft font-semibold text-primary" : "text-text hover:bg-bg"
              }`}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </nav>

        <div>
          {tab === "organization" && (
            <div>
              <h2 className="mb-1 text-lg font-extrabold text-ink">Organization</h2>
              <p className="mb-7 text-sm text-muted">Branding and platform defaults for this tenant.</p>

              {orgLoading && <div role="status" className="py-4 text-sm text-muted">Loading organization settings…</div>}

              {!orgLoading && orgDenied && (
                <DeniedNotice
                  text="You don't have access to view organization settings. This requires the config:read scope."
                  supportReference={orgDenied.supportReference}
                  capability={orgDenied.capability}
                />
              )}

              {!orgLoading && !orgDenied && orgError && (
                <ErrorNotice text={`Failed to load organization settings. ${plainError(orgError)}`} code={orgError.reasonCode} onRetry={loadOrg} supportReference={orgError.supportReference} />
              )}

              {!orgLoading && !orgDenied && !orgError && !canReadSettings && (
                <DeniedNotice text="Your role does not include organization settings access." />
              )}

              {!orgLoading && !orgDenied && !orgError && canReadSettings && (
                <>
                  {orgStale && (
                    <div className="mb-5 rounded-lg border border-warning bg-warning-soft px-4 py-2 text-sm text-warning">
                      These settings were changed by someone else. <button type="button" className="underline" onClick={loadOrg}>Reload</button> before saving again.
                    </div>
                  )}

                  <div className="mb-6">
                    <div className="mb-3 text-sm font-bold text-ink">Branding</div>
                    <div className="mb-5 grid gap-4 sm:grid-cols-2">
                      <Field label="Company Name">
                        <TextInput value={companyName} onChange={setCompanyName} readOnly={!canEditSettings} ariaLabel="Company Name" />
                      </Field>
                      <Field label="Logo URL" hint="Bounded https asset URL. There is no file-upload endpoint yet.">
                        <TextInput value={logoUrl} onChange={setLogoUrl} readOnly={!canEditSettings} placeholder="https://…" ariaLabel="Logo URL" />
                      </Field>
                    </div>
                    <Field label="Primary Color" className="mb-5 sm:w-1/2 sm:pr-2">
                      <Select value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} disabled={!canEditSettings} className="w-full" aria-label="Primary Color">
                        {PRIMARY_COLOR_OPTIONS.map((c) => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </Select>
                    </Field>
                  </div>

                  <div className="mb-6">
                    <div className="mb-3 text-sm font-bold text-ink">Platform Defaults</div>
                    <div className="mb-5 grid gap-4 sm:grid-cols-2">
                      <Field label="Auto-Approve Transfer Threshold" hint="0–100,000">
                        <input
                          type="number"
                          min={0}
                          max={100000}
                          value={autoApproveThreshold}
                          readOnly={!canEditSettings}
                          onChange={(e) => setAutoApproveThreshold(Number(e.target.value))}
                          aria-label="Auto-Approve Transfer Threshold"
                          className="w-full rounded-md border border-line-strong bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-primary"
                        />
                      </Field>
                      <Field label="Default Transfer Currency">
                        <Select value={defaultCurrency} onChange={(e) => setDefaultCurrency(e.target.value)} disabled={!canEditSettings} className="w-full" aria-label="Default Transfer Currency">
                          {CURRENCY_OPTIONS.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </Select>
                      </Field>
                      <Field label="Session Idle Timeout (minutes)" hint="5–240">
                        <input
                          type="number"
                          min={5}
                          max={240}
                          value={idleTimeout}
                          readOnly={!canEditSettings}
                          onChange={(e) => setIdleTimeout(Number(e.target.value))}
                          aria-label="Session Idle Timeout (minutes)"
                          className="w-full rounded-md border border-line-strong bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-primary"
                        />
                      </Field>
                    </div>
                    <label className="flex items-center gap-2.5">
                      <Checkbox
                        checked={emailEnabled}
                        onCheckedChange={(v) => canEditSettings && setEmailEnabled(v)}
                        ariaLabel="Email notifications enabled"
                        disabled={!canEditSettings}
                      />
                      <span className="text-sm text-ink">Email notifications enabled (tenant-wide)</span>
                    </label>
                  </div>

                  <div className="mb-6">
                    <div className="mb-2 text-sm font-bold text-ink">Managed by Alloy</div>
                    <div className="rounded-md border border-dashed border-line bg-bg px-4 py-3">
                      <p className="mb-2 text-xs text-muted">
                        Organization settings are managed by Alloy during the pilot.
                      </p>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {NOT_AVAILABLE_FIELDS.map((f) => (
                          <div key={f.label} className="text-xs">
                            <span className="font-semibold text-ink">{f.label}: </span>
                            <UnavailableValue />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {canEditSettings && <Button onClick={saveOrg} loading={orgSaving}>Save Changes</Button>}
                </>
              )}
            </div>
          )}

          {tab === "notifications" && (
            <div>
              <h2 className="mb-1 text-lg font-extrabold text-ink">Notifications</h2>
              <p className="mb-7 text-sm text-muted">Configure email, in-app, and webhook delivery per event type.</p>

              {notifLoading && <div role="status" className="py-4 text-sm text-muted">Loading notification preferences…</div>}

              {!notifLoading && notifDenied && (
                <DeniedNotice
                  text="You don't have access to notification preferences. This requires the notifications:read scope."
                  supportReference={notifDenied.supportReference}
                  capability={notifDenied.capability}
                />
              )}

              {!notifLoading && !notifDenied && notifError && (
                <ErrorNotice text={`Failed to load notification preferences. ${plainError(notifError)}`} code={notifError.reasonCode} onRetry={loadNotifications} supportReference={notifError.supportReference} />
              )}

              {!notifLoading && !notifDenied && !notifError && !canReadNotifications && (
                <DeniedNotice text="Your role does not include notification preferences access." />
              )}

              {!notifLoading && !notifDenied && !notifError && canReadNotifications && (
                <>
                  {Object.keys(channels).length === 0 && (
                    <div className="mb-5 rounded-md border border-line bg-bg px-4 py-3 text-sm text-muted">
                      No notification event types are configured for this tenant yet.
                    </div>
                  )}
                  <div className="mb-5 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-line text-left text-xs font-semibold text-muted">
                          <th className="py-2 pr-4">Event</th>
                          <th className="px-2 py-2 text-center">Email</th>
                          <th className="px-2 py-2 text-center">In-App</th>
                          <th className="px-2 py-2 text-center">Webhook</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-line">
                        {Object.keys(channels).sort().map((eventType) => {
                          const meta = eventTypeLabel(eventType);
                          const config = channels[eventType];
                          return (
                            <tr key={eventType}>
                              <td className="py-2.5 pr-4">
                                <div className="text-sm font-medium text-ink">{meta.label}</div>
                                {meta.desc && <div className="text-xs text-muted">{meta.desc}</div>}
                              </td>
                              {(["email", "inApp", "webhook"] as const).map((field) => (
                                <td key={field} className="px-2 py-2.5 text-center">
                                  <Checkbox
                                    checked={config[field]}
                                    onCheckedChange={(v) => canEditNotifications && setChannel(eventType, field, v)}
                                    ariaLabel={`${meta.label} — ${field}`}
                                    disabled={!canEditNotifications}
                                  />
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {canEditNotifications && Object.keys(channels).length > 0 && (
                    <Button onClick={saveNotifications} loading={notifSaving}>Save Preferences</Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DeniedNotice({ text, supportReference, capability }: { text: string; supportReference?: string; capability?: string }) {
  return (
    <div role="status" className="rounded-lg border border-line bg-bg px-4 py-3 text-sm text-muted">
      {text}
      <SupportDetails reference={supportReference} capability={capability} />
    </div>
  );
}

function ErrorNotice({ text, onRetry, supportReference, code }: { text: string; onRetry: () => void; supportReference?: string; code?: string }) {
  return (
    <div role="alert" className="rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm text-danger">
      {text}{" "}
      <button type="button" className="underline" onClick={onRetry}>Retry</button>
      {/* ALL-5774: the code is a secondary detail for support, never the message. */}
      {code && <span className="mt-1 block font-mono text-xs">Code: {code}</span>}
      <CopyableReference reference={supportReference} compact className="mt-1.5" />
    </div>
  );
}

function TextInput({ value, onChange, readOnly, placeholder, ariaLabel }: { value: string; onChange: (v: string) => void; readOnly?: boolean; placeholder?: string; ariaLabel?: string }) {
  return (
    <input
      type="text"
      value={value}
      readOnly={readOnly}
      placeholder={placeholder}
      // `Field` renders its label as a sibling, not a wrapper, so the control
      // has no accessible name without this (see the accessible-name tests).
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)] ${
        readOnly ? "cursor-default border-line bg-bg text-muted" : "border-line-strong bg-surface text-ink"
      }`}
    />
  );
}

function Field({ label, hint, className, children }: { label: string; hint?: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-semibold text-ink">{label}</label>
      {children}
      {hint && <div className="mt-1 text-xs text-muted">{hint}</div>}
    </div>
  );
}
